import {
  classifyPointFilterToken,
  combineOrdinateFilter,
  midBetween,
  type OrdinateFilterAxes,
} from "./pointFilterTokens";

export type CoordinatePoint = {
  x: number;
  y: number;
  z: number;
};

export type CoordinateInputErrorCode =
  | "EMPTY_INPUT"
  | "INVALID_TOKEN_COUNT"
  | "INVALID_NUMBER"
  | "NON_POSITIVE_RADIUS"
  | "MISSING_REFERENCE_POINT"
  | "MISSING_DIRECTION_POINT";

export type CoordinateInputError = {
  code: CoordinateInputErrorCode;
  message: string;
  input: string;
  token?: string;
  expectedTokens?: number;
  receivedTokens?: number;
};

export type ParseResult<T> =
  | {
      ok: true;
      value: T;
    }
  | {
      ok: false;
      error: CoordinateInputError;
    };

export type LineCommandInput = {
  kind: "line";
  start: CoordinatePoint;
  end: CoordinatePoint;
};

export type PointCommandInput = {
  kind: "point";
  location: CoordinatePoint;
};

export type CircleCommandInput = {
  kind: "circle";
  center: CoordinatePoint;
  radius: number;
};

export type ArcCommandInput = {
  kind: "arc";
  center: CoordinatePoint;
  radius: number;
  startAngle: number;
  endAngle: number;
};

export type GeometryCommandInput =
  | LineCommandInput
  | PointCommandInput
  | CircleCommandInput
  | ArcCommandInput;

/**
 * Point-filter / temporary-tracking accumulator threaded through
 * `parsePointToken` so a single token resolves the CONTINUATION of an
 * in-progress FROM / M2P / `.X.Y.Z` flow (ТЗ §8.6/§10.4). Exactly one of
 * the three fields is populated per continuation; the interactive session
 * that owns the multi-step flow (`pointFilterSession.ts` in the web app)
 * fills it in from its accumulated state.
 */
export type PointFilterState = {
  /**
   * FROM base point: the current token is the OFFSET (`@dx,dy` / polar /
   * absolute) measured from this captured base.
   */
  fromBase?: CoordinatePoint | null;
  /**
   * M2P / MTP first point: the current token is the SECOND point; the
   * result is the midpoint of the two.
   */
  midFirst?: CoordinatePoint | null;
  /**
   * Coordinate-filter accumulator: the ordinates already supplied by a
   * `.X`/`.Y`/`.Z` (or `.XY`/…) filter; the current token supplies the
   * remaining ordinates.
   */
  ordinateFilter?: {
    axes: OrdinateFilterAxes;
    supplied: CoordinatePoint;
  } | null;
};

export type PointTokenOptions = {
  referencePoint?: CoordinatePoint | null;
  directionPoint?: CoordinatePoint | null;
  filter?: PointFilterState | null;
};

function success<T>(value: T): ParseResult<T> {
  return {
    ok: true,
    value
  };
}

function failure(
  code: CoordinateInputErrorCode,
  message: string,
  input: string,
  extras: Partial<CoordinateInputError> = {}
): ParseResult<never> {
  return {
    ok: false,
    error: {
      code,
      message,
      input,
      ...extras
    }
  };
}

function tokenize(input: string): string[] {
  return input.trim().split(/\s+/u).filter(Boolean);
}

function parseFiniteNumber(input: string, token: string): ParseResult<number> {
  const value = Number(token);

  if (token.trim().length === 0 || Number.isNaN(value) || !Number.isFinite(value)) {
    return failure("INVALID_NUMBER", `Invalid numeric value: ${token}`, input, {
      token
    });
  }

  return success(value);
}

function parseRelativePointToken(
  input: string,
  token: string,
  referencePoint: CoordinatePoint | null | undefined
): ParseResult<CoordinatePoint> {
  if (!referencePoint) {
    return failure(
      "MISSING_REFERENCE_POINT",
      "Relative coordinates require a reference point.",
      input
    );
  }

  const relativeToken = token.slice(1).trim();
  const parsed = parseAbsolutePointToken(relativeToken);

  if (!parsed.ok) {
    return parsed;
  }

  return success({
    x: referencePoint.x + parsed.value.x,
    y: referencePoint.y + parsed.value.y,
    z: referencePoint.z + parsed.value.z
  });
}

function parsePolarPointToken(
  input: string,
  token: string,
  referencePoint: CoordinatePoint | null | undefined
): ParseResult<CoordinatePoint> {
  if (!referencePoint) {
    return failure(
      "MISSING_REFERENCE_POINT",
      "Polar coordinates require a reference point.",
      input
    );
  }

  const polarToken = token.slice(1).trim();
  const [distanceToken, angleToken] = polarToken.split("<").map((part) => part.trim());

  if (!distanceToken || !angleToken) {
    return failure(
      "INVALID_TOKEN_COUNT",
      "Polar coordinates must use @distance<angle syntax.",
      input
    );
  }

  const distance = parseFiniteNumber(input, distanceToken);
  if (!distance.ok) {
    return distance;
  }

  const angle = parseFiniteNumber(input, angleToken);
  if (!angle.ok) {
    return angle;
  }

  // AutoCAD-parity convention: angle is measured in **math convention** —
  // 0° = East (positive X axis), 90° = North (positive Y axis), CCW positive.
  // This matches the convention used by `polarFromPoints` in
  // apps/web/src/app/dynamicInput/fieldGeometry.ts, which is the source of
  // the live `@dist<angle` token emitted by Dynamic Input. Previously the
  // parser applied a `-90` azimuth shift that disagreed with the emitter,
  // rotating every typed/committed polar point 90° CW from its intended
  // direction (e.g. `@100<0` produced (0,-100) instead of (100,0)) — that
  // disagreement was the root cause of the COPY rotated-destination bug.
  const radians = (angle.value * Math.PI) / 180;
  return success({
    x: referencePoint.x + distance.value * Math.cos(radians),
    y: referencePoint.y + distance.value * Math.sin(radians),
    z: referencePoint.z
  });
}

function parseDirectDistanceToken(
  input: string,
  token: string,
  options: PointTokenOptions
): ParseResult<CoordinatePoint> {
  if (!options.referencePoint) {
    return failure(
      "MISSING_REFERENCE_POINT",
      "Direct distance input requires a reference point.",
      input
    );
  }

  if (!options.directionPoint) {
    return failure(
      "MISSING_DIRECTION_POINT",
      "Direct distance input requires a cursor direction.",
      input
    );
  }

  const distance = parseFiniteNumber(input, token);
  if (!distance.ok) {
    return distance;
  }

  const dx = options.directionPoint.x - options.referencePoint.x;
  const dy = options.directionPoint.y - options.referencePoint.y;
  const magnitude = Math.hypot(dx, dy);

  if (magnitude === 0) {
    return failure(
      "MISSING_DIRECTION_POINT",
      "Direct distance input requires a non-zero cursor direction.",
      input
    );
  }

  return success({
    x: options.referencePoint.x + (dx / magnitude) * distance.value,
    y: options.referencePoint.y + (dy / magnitude) * distance.value,
    z: options.referencePoint.z
  });
}

export function parseAbsolutePointToken(input: string): ParseResult<CoordinatePoint> {
  const token = input.trim();

  if (token.length === 0) {
    return failure("EMPTY_INPUT", "Point token is empty.", input);
  }

  const parts = token.split(",").map((part) => part.trim());

  if (parts.length < 2 || parts.length > 3) {
    return failure(
      "INVALID_TOKEN_COUNT",
      "Point token must contain x,y or x,y,z.",
      input,
      {
        expectedTokens: 2,
        receivedTokens: parts.length
      }
    );
  }

  const [xToken, yToken, zToken] = parts;
  const xResult = parseFiniteNumber(input, xToken ?? "");

  if (!xResult.ok) {
    return xResult;
  }

  const yResult = parseFiniteNumber(input, yToken ?? "");

  if (!yResult.ok) {
    return yResult;
  }

  const zResult = zToken === undefined ? success(0) : parseFiniteNumber(input, zToken);

  if (!zResult.ok) {
    return zResult;
  }

  return success({
    x: xResult.value,
    y: yResult.value,
    z: zResult.value
  });
}

export function parsePointToken(
  input: string,
  options: PointTokenOptions = {}
): ParseResult<CoordinatePoint> {
  const token = input.trim();

  if (token.length === 0) {
    return failure("EMPTY_INPUT", "Point token is empty.", input);
  }

  // ── Point-filter / temporary-tracking continuations (ТЗ §8.6/§10.4) ──
  // When the interactive session has captured an earlier operand, this
  // token is the CONTINUATION that completes the composed point. Each
  // continuation re-parses the token as an ordinary coordinate (with the
  // appropriate reference) via the shared branches below, then applies the
  // filter arithmetic from `pointFilterTokens.ts`.
  const filterState = options.filter;
  if (filterState?.fromBase) {
    // FROM: the offset is measured from the captured base, so the base
    // becomes the reference point for a relative/polar/direct token. An
    // absolute token ignores the base (AutoCAD parity).
    return parsePointToken(token, {
      referencePoint: filterState.fromBase,
      directionPoint: options.directionPoint,
    });
  }
  if (filterState?.midFirst) {
    // The captured first point IS the sub-prompt's last point, so a
    // relative/polar/direct second token measures from it (AutoCAD parity —
    // the interactive web session anchors the M2P continuation on the captured
    // first point, and the package-level API must be symmetric). It takes
    // priority over any host `referencePoint`; an absolute token ignores it.
    // (Codex PR #504 P3.)
    const second = parsePointToken(token, {
      referencePoint: filterState.midFirst,
      directionPoint: options.directionPoint,
    });
    if (!second.ok) {
      return second;
    }
    return success(midBetween(filterState.midFirst, second.value));
  }
  if (filterState?.ordinateFilter) {
    // The point that supplied the first ordinate anchors the rest
    // continuation: a relative/polar/direct rest token measures from it, not
    // from the host reference (mirrors the web session's rest-stage anchor).
    // (Codex PR #504 P3.)
    const rest = parsePointToken(token, {
      referencePoint: filterState.ordinateFilter.supplied,
      directionPoint: options.directionPoint,
    });
    if (!rest.ok) {
      return rest;
    }
    return success(
      combineOrdinateFilter(
        filterState.ordinateFilter.axes,
        filterState.ordinateFilter.supplied,
        rest.value,
      ),
    );
  }

  // A BARE filter starter (`FROM`, `M2P`/`MTP`, `.X`…) is not a complete
  // point on its own — it opens an interactive tracking session. Report an
  // incomplete-token failure (existing code) so a stray filter token typed
  // outside a live session does not mis-parse; the session layer intercepts
  // these before ever reaching this fallback.
  if (classifyPointFilterToken(token)) {
    return failure(
      "INVALID_TOKEN_COUNT",
      `Point-filter token "${token}" requires an interactive tracking session.`,
      input,
      { token },
    );
  }

  if (token.startsWith("@") && token.includes("<")) {
    return parsePolarPointToken(input, token, options.referencePoint);
  }

  if (token.startsWith("@")) {
    return parseRelativePointToken(input, token, options.referencePoint);
  }

  if (/^-?\d+(?:\.\d+)?$/u.test(token)) {
    return parseDirectDistanceToken(input, token, options);
  }

  return parseAbsolutePointToken(input);
}

export function parseLineCommandInput(input: string): ParseResult<LineCommandInput> {
  const tokens = tokenize(input);

  if (tokens.length === 0) {
    return failure("EMPTY_INPUT", "LINE command input is empty.", input);
  }

  if (tokens.length !== 2) {
    return failure(
      "INVALID_TOKEN_COUNT",
      "LINE command expects exactly two point tokens.",
      input,
      {
        expectedTokens: 2,
        receivedTokens: tokens.length
      }
    );
  }

  const start = parseAbsolutePointToken(tokens[0] ?? "");

  if (!start.ok) {
    return start;
  }

  const end = parseAbsolutePointToken(tokens[1] ?? "");

  if (!end.ok) {
    return end;
  }

  return success({
    kind: "line",
    start: start.value,
    end: end.value
  });
}

export function parsePointCommandInput(input: string): ParseResult<PointCommandInput> {
  const tokens = tokenize(input);

  if (tokens.length === 0) {
    return failure("EMPTY_INPUT", "POINT command input is empty.", input);
  }

  if (tokens.length !== 1) {
    return failure(
      "INVALID_TOKEN_COUNT",
      "POINT command expects exactly one location token.",
      input,
      {
        expectedTokens: 1,
        receivedTokens: tokens.length
      }
    );
  }

  const location = parseAbsolutePointToken(tokens[0] ?? "");

  if (!location.ok) {
    return location;
  }

  return success({
    kind: "point",
    location: location.value
  });
}

export function parseCircleCommandInput(input: string): ParseResult<CircleCommandInput> {
  const tokens = tokenize(input);

  if (tokens.length === 0) {
    return failure("EMPTY_INPUT", "CIRCLE command input is empty.", input);
  }

  if (tokens.length !== 2) {
    return failure(
      "INVALID_TOKEN_COUNT",
      "CIRCLE command expects a point token and a radius.",
      input,
      {
        expectedTokens: 2,
        receivedTokens: tokens.length
      }
    );
  }

  const center = parseAbsolutePointToken(tokens[0] ?? "");

  if (!center.ok) {
    return center;
  }

  const radiusToken = tokens[1];

  if (radiusToken === undefined) {
    return failure(
      "INVALID_TOKEN_COUNT",
      "CIRCLE command expects a point token and a radius.",
      input,
      {
        expectedTokens: 2,
        receivedTokens: tokens.length
      }
    );
  }

  const radius = parseFiniteNumber(input, radiusToken);

  if (!radius.ok) {
    return radius;
  }

  if (radius.value <= 0) {
    return failure(
      "NON_POSITIVE_RADIUS",
      "Circle radius must be greater than zero.",
      input,
      {
        token: radiusToken
      }
    );
  }

  return success({
    kind: "circle",
    center: center.value,
    radius: radius.value
  });
}

export function parseArcCommandInput(input: string): ParseResult<ArcCommandInput> {
  const tokens = tokenize(input);

  if (tokens.length === 0) {
    return failure("EMPTY_INPUT", "ARC command input is empty.", input);
  }

  if (tokens.length !== 4) {
    return failure(
      "INVALID_TOKEN_COUNT",
      "ARC command expects a point token, radius, start angle and end angle.",
      input,
      {
        expectedTokens: 4,
        receivedTokens: tokens.length
      }
    );
  }

  const center = parseAbsolutePointToken(tokens[0] ?? "");

  if (!center.ok) {
    return center;
  }

  const radiusToken = tokens[1];

  if (radiusToken === undefined) {
    return failure(
      "INVALID_TOKEN_COUNT",
      "ARC command expects a point token, radius, start angle and end angle.",
      input,
      {
        expectedTokens: 4,
        receivedTokens: tokens.length
      }
    );
  }

  const radius = parseFiniteNumber(input, radiusToken);

  if (!radius.ok) {
    return radius;
  }

  if (radius.value <= 0) {
    return failure(
      "NON_POSITIVE_RADIUS",
      "Arc radius must be greater than zero.",
      input,
      {
        token: radiusToken
      }
    );
  }

  const startAngle = parseFiniteNumber(input, tokens[2] ?? "");

  if (!startAngle.ok) {
    return startAngle;
  }

  const endAngle = parseFiniteNumber(input, tokens[3] ?? "");

  if (!endAngle.ok) {
    return endAngle;
  }

  return success({
    kind: "arc",
    center: center.value,
    radius: radius.value,
    startAngle: startAngle.value,
    endAngle: endAngle.value
  });
}

export function parseGeometryCommandInput(
  kind: GeometryCommandInput["kind"],
  input: string
): ParseResult<GeometryCommandInput> {
  switch (kind) {
    case "line":
      return parseLineCommandInput(input);
    case "circle":
      return parseCircleCommandInput(input);
    case "arc":
      return parseArcCommandInput(input);
  }
}

export function formatCoordinatePoint(point: CoordinatePoint): string {
  return point.z === 0 ? `${point.x},${point.y}` : `${point.x},${point.y},${point.z}`;
}
